import { useQuery } from "@tanstack/react-query";
import { reportsApi, type ReportLibraryQuery } from "@/api/ReportsApi";
import { useAuth } from "@/components/contexts/AuthContext/UseAuth";
import { ReportRequestError } from "@/api/reportRequest";

// The report list and saved views follow the account; market report links are public.
export function useReports(filters: ReportLibraryQuery = {}) {
  const { user, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["report-library", user?.profile.sub, filters],
    queryFn: ({ signal }) => reportsApi.library(filters, signal),
    enabled: isAuthenticated,
    gcTime: 0,
  });
}
export function useReport(id?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["reports", user?.profile.sub, id],
    queryFn: ({ signal }) => reportsApi.report(id!, signal),
    retry: (failureCount, error) =>
      !(
        error instanceof ReportRequestError && [401, 404].includes(error.status)
      ) && failureCount < 3,
    enabled: !!id,
    gcTime: 0,
  });
}
export function useFollowing() {
  const { user, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["saved-views", user?.profile.sub],
    queryFn: ({ signal }) => reportsApi.views(signal),
    enabled: isAuthenticated,
    gcTime: 0,
  });
}
export function useSavedView(id?: string) {
  const { user, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["saved-views", user?.profile.sub, id],
    queryFn: ({ signal }) => reportsApi.view(id!, signal),
    enabled: isAuthenticated && !!id,
    gcTime: 0,
    refetchInterval: (query) => (query.state.data?.refreshing ? 2000 : false),
  });
}
